import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { etDateTimeLocalToIso } from "@/lib/et-datetime";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { SocialPreview } from "@/components/social/SocialPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  socialEvergreenList,
  socialEvergreenRescan,
  socialEvergreenReuse,
  socialEvergreenSetFlag,
  socialEvergreenTags,
} from "@/lib/social-evergreen.functions";
import {
  AlertTriangle,
  Copy,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
  Star,
  StarOff,
  Tag as TagIcon,
} from "lucide-react";

export const Route = createFileRoute("/admin/social/evergreen")({
  component: EvergreenPage,
  head: () => ({
    meta: [
      { title: "Evergreen social library · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type EvergreenRow = {
  id: string;
  body: string;
  platforms: string[];
  media_urls: string[] | null;
  link_url: string | null;
  tags: string[] | null;
  compliance_status: string;
  compliance_findings: any;
  updated_at: string;
};

const STATUS_FILTERS = ["all", "clean", "warn", "blocked", "pending"] as const;

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    clean: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    blocked: "bg-rose-50 text-rose-700 border-rose-200",
    pending: "bg-slate-50 text-slate-700 border-slate-200",
  };
  const cls = map[status] ?? map.pending;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${cls}`}>
      <ShieldCheck className="h-3 w-3" /> {status}
    </span>
  );
}

function EvergreenPage() {
  const qc = useQueryClient();
  const list = useServerFn(socialEvergreenList);
  const tags = useServerFn(socialEvergreenTags);
  const setFlag = useServerFn(socialEvergreenSetFlag);
  const rescan = useServerFn(socialEvergreenRescan);
  const reuse = useServerFn(socialEvergreenReuse);

  const [query, setQuery] = useState("");
  const [tag, setTag] = useState<string>("");
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>("all");
  const [reuseTarget, setReuseTarget] = useState<EvergreenRow | null>(null);
  const [reuseWhen, setReuseWhen] = useState<string>("");
  const [reusePlatforms, setReusePlatforms] = useState<string>("");
  const [reuseAckWarnings, setReuseAckWarnings] = useState(false);
  const [reuseBypassDedupe, setReuseBypassDedupe] = useState(false);
  const [reuseErr, setReuseErr] = useState<{ code?: string; message: string; findings?: any } | null>(null);

  const listQuery = useQuery({
    queryKey: ["social-evergreen", { query, tag, status }],
    queryFn: () => list({ data: { query: query || undefined, tag: tag || undefined, status } }),
  });
  const tagsQuery = useQuery({ queryKey: ["social-evergreen-tags"], queryFn: () => tags() });

  const rows = useMemo(() => (listQuery.data ?? []) as EvergreenRow[], [listQuery.data]);
  const availableTags = (tagsQuery.data ?? []) as string[];

  async function handleToggleEvergreen(row: EvergreenRow, next: boolean) {
    try {
      await setFlag({ data: { id: row.id, is_evergreen: next } });
      toast.success(next ? "Added to evergreen library" : "Removed from evergreen library");
      await qc.invalidateQueries({ queryKey: ["social-evergreen"] });
    } catch (err: any) {
      toast.error(err?.message ?? "Update failed");
    }
  }

  async function handleUpdateTags(row: EvergreenRow, tagInput: string) {
    const next = tagInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 20);
    try {
      await setFlag({ data: { id: row.id, is_evergreen: true, tags: next } });
      toast.success("Tags updated");
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["social-evergreen"] }),
        qc.invalidateQueries({ queryKey: ["social-evergreen-tags"] }),
      ]);
    } catch (err: any) {
      toast.error(err?.message ?? "Tag update failed");
    }
  }

  async function handleRescan(row: EvergreenRow) {
    try {
      const res: any = await rescan({ data: { id: row.id } });
      const s = res?.scan?.status ?? "pending";
      if (s === "blocked") toast.error("Rescan: compliance ERRORS — see findings.", { duration: 8000 });
      else if (s === "warn") toast.warning("Rescan: warnings present — acknowledge before reuse.");
      else toast.success("Rescan clean.");
      await qc.invalidateQueries({ queryKey: ["social-evergreen"] });
    } catch (err: any) {
      toast.error(err?.message ?? "Rescan failed");
    }
  }

  function openReuse(row: EvergreenRow) {
    setReuseTarget(row);
    setReusePlatforms((row.platforms ?? []).join(","));
    setReuseWhen("");
    setReuseAckWarnings(false);
    setReuseBypassDedupe(false);
    setReuseErr(null);
  }

  async function submitReuse() {
    if (!reuseTarget) return;
    const platforms = reusePlatforms
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (platforms.length === 0) {
      setReuseErr({ message: "At least one platform is required." });
      return;
    }
    let scheduled_at: string | null = null;
    if (reuseWhen) {
      scheduled_at = etDateTimeLocalToIso(reuseWhen);
    }
    try {
      const res: any = await reuse({
        data: {
          id: reuseTarget.id,
          platforms,
          scheduled_at,
          status: scheduled_at ? "scheduled" : "draft",
          acknowledge_warnings: reuseAckWarnings,
          bypass_dedupe: reuseBypassDedupe,
        },
      });
      if (res?.error) {
        setReuseErr(res.error);
        return;
      }
      toast.success(scheduled_at ? "Scheduled from evergreen." : "Draft created from evergreen.");
      setReuseTarget(null);
      await qc.invalidateQueries({ queryKey: ["social-evergreen"] });
      await qc.invalidateQueries({ queryKey: ["social-posts"] });
    } catch (err: any) {
      setReuseErr({ message: err?.message ?? "Reuse failed" });
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <h1 className="mb-2 text-2xl font-bold">Evergreen social library</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Reusable, brand-approved posts. Every reuse re-runs the reverse-mortgage compliance scan and
        checks for duplicate posts already scheduled in the next 14 days.
      </p>
      <SocialSubNav />

      <div className="mb-4 grid gap-3 md:grid-cols-4">
        <div>
          <Label htmlFor="ev-search">Search</Label>
          <Input
            id="ev-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search body or tag"
          />
        </div>
        <div>
          <Label htmlFor="ev-tag">Tag</Label>
          <select
            id="ev-tag"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">All tags</option>
            {availableTags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="ev-status">Compliance</Label>
          <select
            id="ev-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All statuses" : s}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => qc.invalidateQueries({ queryKey: ["social-evergreen"] })}
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {listQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No evergreen posts yet. Mark any draft or published post as evergreen from its edit view to
          build your reusable library.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {rows.map((row) => (
            <article key={row.id} className="rounded-lg border border-border bg-card p-4 shadow-card">
              <header className="mb-3 flex items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                    <Star className="h-3 w-3" /> Evergreen
                  </span>
                  <StatusBadge status={row.compliance_status ?? "pending"} />
                  {(row.platforms ?? []).map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs capitalize text-muted-foreground"
                    >
                      {p}
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleRescan(row)} title="Rescan compliance">
                    <ShieldCheck className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleEvergreen(row, false)}
                    title="Remove from evergreen"
                  >
                    <StarOff className="h-4 w-4" />
                  </Button>
                </div>
              </header>

              <SocialPreview body={row.body} mediaUrls={row.media_urls ?? []} />

              <div className="mt-3">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  <TagIcon className="mr-1 inline h-3 w-3" /> Tags (comma-separated)
                </Label>
                <div className="flex gap-2">
                  <Input
                    defaultValue={(row.tags ?? []).join(", ")}
                    onBlur={(e) => {
                      const next = e.target.value;
                      const original = (row.tags ?? []).join(", ");
                      if (next.trim() !== original) handleUpdateTags(row, next);
                    }}
                    placeholder="e.g. dscr, cash-out, section-8"
                  />
                </div>
              </div>

              {row.compliance_status === "warn" && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  <AlertTriangle className="mr-1 inline h-3 w-3" /> Warnings present — reuse will require
                  acknowledgement.
                </div>
              )}
              {row.compliance_status === "blocked" && (
                <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                  <AlertTriangle className="mr-1 inline h-3 w-3" /> Blocked — cannot reuse until compliance
                  errors are resolved.
                </div>
              )}

              <footer className="mt-3 flex justify-end">
                <Button
                  size="sm"
                  onClick={() => openReuse(row)}
                  disabled={row.compliance_status === "blocked"}
                >
                  <Copy className="mr-2 h-4 w-4" /> Reuse
                </Button>
              </footer>
            </article>
          ))}
        </div>
      )}

      <Dialog open={!!reuseTarget} onOpenChange={(open) => !open && setReuseTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Sparkles className="mr-1 inline h-4 w-4" /> Reuse evergreen post
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label htmlFor="reuse-platforms">Platforms (comma-separated)</Label>
              <Input
                id="reuse-platforms"
                value={reusePlatforms}
                onChange={(e) => setReusePlatforms(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="reuse-when">Schedule for, Eastern Time (leave blank for draft)</Label>
              <Input
                id="reuse-when"
                type="datetime-local"
                value={reuseWhen}
                onChange={(e) => setReuseWhen(e.target.value)}
              />
            </div>
            {reuseErr && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                <p className="font-semibold">{reuseErr.message}</p>
                {reuseErr.code === "REQUIRES_ACKNOWLEDGE" && (
                  <label className="mt-2 flex items-center gap-2 text-rose-900">
                    <input
                      type="checkbox"
                      checked={reuseAckWarnings}
                      onChange={(e) => setReuseAckWarnings(e.target.checked)}
                    />
                    I acknowledge the compliance warnings above and take responsibility for republishing.
                  </label>
                )}
                {reuseErr.code === "DUPLICATE_NEARBY" && (
                  <label className="mt-2 flex items-center gap-2 text-rose-900">
                    <input
                      type="checkbox"
                      checked={reuseBypassDedupe}
                      onChange={(e) => setReuseBypassDedupe(e.target.checked)}
                    />
                    Post anyway (override 14-day duplicate guard).
                  </label>
                )}
                {reuseErr.findings && Array.isArray(reuseErr.findings) && (
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {reuseErr.findings.slice(0, 8).map((f: any, i: number) => (
                      <li key={i}>
                        <span className="uppercase">[{f.severity}]</span> {f.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReuseTarget(null)}>
              Cancel
            </Button>
            <Button onClick={submitReuse}>
              <Copy className="mr-2 h-4 w-4" /> Create copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
